import {getGeocode, getLatLng,} from "use-places-autocomplete";
import Axios from 'axios'

/**
 * optimizeInspections.js
 * 
 * This file contains functions to transform inspection assignments into a format
 * suitable for route optimization. It takes the assignments data from SelectionDashboard.js
 * and converts it into a format that can be used with a route optimization API.
 */

/**
 * Transforms the assignments data into a format suitable for route optimization
 * 
 * @param {Array} assignments - Array of objects containing assignedInspector and assignedInspections
 * @returns {Promise<Array>} - Array of optimized assignments with route information
 */
export async function optimizeInspectorRoutes(assignments) {
  if (!assignments || !Array.isArray(assignments)) {
    console.warn('Invalid assignments data provided to optimizeInspectorRoutes');
    return [];
  }

  // Use Promise.all to handle multiple async operations
  const optimizationParams = await Promise.all(assignments.map(async assignment => {
    const { assignedInspector, assignedInspections } = assignment;
    
    // Skip if no inspections assigned
    if (!assignedInspections || assignedInspections.length === 0) {
      return null;
    }

    // Create the inspector's address string
    const inspectorAddress = formatAddress(
      assignedInspector.Street,
      assignedInspector.City,
      assignedInspector.State,
      assignedInspector.Postcode
    );

    // Create the stop list from assigned inspections
    const stopList = assignedInspections.map(inspection => {
      return {
        address: formatAddress(
          inspection.Street,
          inspection.City,
          inspection.State,
          inspection.Postcode
        ),
        coordinates: [inspection.lat, inspection.lng]
      };
    });

    let inspectorCoordinates = await getCoordinates(inspectorAddress)

    // Create the route optimization object
    return {
      startAddress: {
        address: inspectorAddress,
        coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
      },
      stopList,
      destinationAddress: {
        address: inspectorAddress,
        coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
      },
      travelMethod: "d",
      lang: "en"
    };
  }));

  const filteredParams = optimizationParams.filter(Boolean); // Remove null entries

  // Call the API for each route parameter
  let optimizedRoutes = await optimizeRoutesWithAPI(filteredParams);


  // Create optimizedAssignments array that combines the optimized route data with the original assignment information
  const optimizedAssignments = [];
  
  // Process each optimized route and create the corresponding optimized assignment
  for (let i = 0; i < optimizedRoutes.length; i++) {
    const optimizedRoute = optimizedRoutes[i];
    const originalAssignment = assignments[i];
    
    if (!optimizedRoute || !originalAssignment) {
      console.warn(`Missing data for assignment at index ${i}`);
      continue;
    }
    
    // Extract the waypoint order from the fullApiResponse
    const waypointOrder = optimizedRoute.fullApiResponse.routes[0].waypoint_order;
    
    // Reorder the assignedInspections based on the waypoint order
    let reorderedInspections = []
    for (let j = 0; j < waypointOrder.length; j++) {
      reorderedInspections.push(originalAssignment.assignedInspections[waypointOrder[j]])
    }
    
    // Create the optimized assignment object
    const optimizedAssignment = {
      assignedInspector: originalAssignment.assignedInspector,
      assignedInspections: reorderedInspections,
      curatedLinks: optimizedRoute.curatedLinks,
      legDistances: optimizedRoute.legDistances,
      legTimes: optimizedRoute.legTimes,
      routeOrder: optimizedRoute.routeOrder,
      splitUrls: optimizedRoute.splitUrls,
      totalDistance: optimizedRoute.totalDistance,
      totalTime: optimizedRoute.totalTime
    };
    
    optimizedAssignments.push(optimizedAssignment);
  }

  return optimizedAssignments;
}

/**
 * Calls the route optimization API for each set of parameters
 * 
 * @param {Array} routeParams - Array of route parameters to optimize
 * @returns {Promise<Array>} - Array of optimized routes
 */
async function optimizeRoutesWithAPI(routeParams) {
  if (!routeParams || routeParams.length === 0) {
    console.warn('No route parameters provided to optimizeRoutesWithAPI');
    return [];
  }

  try {
    // Process each route parameter sequentially
    const optimizedRoutes = [];
    
    for (let i = 0; i < routeParams.length; i++) {
      console.log(`Optimizing route ${i+1} of ${routeParams.length}`);
      
      const response = await Axios.post(
        `https://api.routora.com/optimize-app-test/${process.env.REACT_APP_HASHED_GOOGLE_MAPS_API}`,
        routeParams[i]
      );
      
      optimizedRoutes.push(response.data);
    }
    
    return optimizedRoutes;
  } catch (error) {
    console.error('Error optimizing routes:', error);
    throw error; // Re-throw to handle in calling component
  }
}

/**
 * Formats an address string from its components
 * 
 * @param {string} street - Street address
 * @param {string} city - City name
 * @param {string} state - State code
 * @param {string} postcode - Postal code
 * @returns {string} - Formatted address string
 */
function formatAddress(street, city, state, postcode) {
  return `${street} ${city} ${state} ${postcode}`.trim();
}

async function getCoordinates(address) {
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      return { lat, lng }
    //   panTo({ lat, lng });
    } catch (error) {
      console.log("😱 Error: ", error);
    }
};
